/**
 * Campaign Links — the builder that makes a wrong UTM tag unpickable.
 *
 * Why this page exists (2026-08-11)
 * ---------------------------------
 * The taxonomy lived as prose in docs/marketing/utm-taxonomy.md plus hardcoded
 * <option> lists in a standalone `docs/marketing/campaign-link-builder.html`
 * that was served nowhere, tracked by nothing and had already rotted (it still
 * carried a placeholder App Store id). Nobody opens a file they have to clone
 * the repo to run, so links were hand-typed — and `Instagram` vs `instagram`
 * is two channels forever, with no retroactive fix once the link is published.
 *
 * Everything selectable here comes from `@/lib/utm-taxonomy`, the byte-mirrored
 * twin of `backend/src/utils/utm-taxonomy.ts` (the admin bundle cannot resolve
 * backend paths — same reason as pages/growth/index.tsx). The pair is pinned by
 * tests/unit/lint/utm-taxonomy-drift.test.ts, so this page can never offer a
 * value the backend does not recognise.
 *
 * Emission is the STRICT arm of the taxonomy on purpose. Ingest
 * (POST /v1/attribution) normalises and observes but never rejects, because a
 * 400 there discards a real touch forever. Here there is a human at a keyboard
 * who can fix a rejected tuple in two seconds, and a published link cannot be
 * un-published — so the tuple is validated before the URL is even rendered.
 *
 * Gated on `growth.referrals.read`: the audience is exactly the people who
 * already mint /r/ links, and a new permission key would cost a migration
 * against a database stack that is near its 200-CFN-output cap.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { Copy, QrCode, X, Link2, AlertTriangle } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { adminFetch } from '@/providers/admin-api';
import { toast } from '@/hooks/useToast';
import {
  MEDIUM_TO_CHANNEL,
  PROVIDER_SOURCES,
  RESERVED_SOURCES,
  SOURCE_PREFIXES,
  UTM_SOURCES,
  mediumsForSource,
  normalizeTag,
  validateUtmTuple,
  type UtmTupleIssue,
} from '@/lib/utm-taxonomy';

/**
 * Android packages a link may point at. PROD is the public listing; the DEV
 * package is the internal tester rail and is offered only so the team can
 * verify the referrer round-trip on the environment it actually tests on.
 *
 * PREDEV (`ai.myaimatchmaker.abby.predev`) is deliberately absent: it is a
 * DRAFT Play app with internal-only distribution and therefore has no publicly
 * resolvable listing URL, so there is nothing honest to hand someone.
 */
const ANDROID_PACKAGES = [
  { id: 'com.uniquedatingcompany.abby', label: 'PROD — com.uniquedatingcompany.abby' },
  { id: 'ai.myaimatchmaker.abby.dev', label: 'DEV — ai.myaimatchmaker.abby.dev' },
] as const;

/**
 * Play's canonical listing URL. ONLY this form propagates a `referrer` to the
 * install — the tester opt-in pages (`/apps/testing/…`) are a join flow, not a
 * store listing, and hand nothing to `getInstallReferrerAsync()`.
 */
const PLAY_DETAILS_PREFIX = 'https://play.google.com/store/apps/details';

/** PROD App Store listing (ascAppId 6755665408) — see the §6c note below. */
const APP_STORE_URL = 'https://apps.apple.com/app/id6755665408';

const DEFAULT_DESTINATION = 'https://myaimatchmaker.ai/';

/** Emit order for the Play referrer, and the drop order when it must shorten. */
const REFERRER_UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

/**
 * Reduce a free-text value to a referrer-safe token.
 *
 * Mirrors `sanitizeReferrerValue` in frontend/website/src/lib/store-links.js,
 * and for the same reason: the Play referrer is encoded ONCE as a whole string,
 * so a value that itself needed escaping would have its `%20` turned into
 * `%2520`, and Play returns that verbatim — the app would then record the
 * literal text `%20` as part of the campaign name. Restricting values to
 * `[a-z0-9._-]` makes `%` structurally impossible inside the raw string.
 *
 * Applied to the web URL too, so the SAME campaign reports the same strings on
 * web and in the install rather than two near-identical rows.
 */
function sanitizeTagValue(value: string): string {
  const normalized = normalizeTag(value);
  if (normalized == null) return '';
  return normalized
    .replace(/[^a-z0-9._-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^[-._]+|[-._]+$/g, '')
    .slice(0, 255);
}

/** Human sentence per taxonomy issue — the builder must say what to DO. */
function issueText(issue: UtmTupleIssue, source: string): string {
  switch (issue) {
    case 'source_missing':
      return 'Pick a source.';
    case 'source_off_taxonomy':
      return `"${source}" is not a canonical source. Pick one from the list, or use an influencer_/partner_ family.`;
    case 'source_reserved':
      return `"${source}" is a DERIVED channel (${RESERVED_SOURCES.join(', ')}), never a source — tagging it corrupts the channel report.`;
    case 'medium_missing':
      return 'Pick a medium — it is the parameter the channel is derived from.';
    case 'medium_off_taxonomy':
      return 'That medium is not in the taxonomy. An unmappable medium becomes channel "unknown", which the roll-up filters out entirely.';
    case 'medium_not_valid_for_source':
      return `That medium is not one we use with "${source}".`;
    case 'campaign_missing':
      return 'Enter a campaign.';
    case 'campaign_malformed':
      return 'Campaign must be YYYYMM_shortname — lowercase, underscores (e.g. 202608_launch).';
    default:
      return issue;
  }
}

export function CampaignLinkBuilderPage() {
  const apiUrl = useApiUrl();

  // `sourceKey` is either a canonical source or one of the open-ended family
  // prefixes; a family reveals the suffix field below it.
  const [sourceKey, setSourceKey] = useState('');
  const [sourceSuffix, setSourceSuffix] = useState('');
  const [medium, setMedium] = useState('');
  const [campaign, setCampaign] = useState('');
  const [content, setContent] = useState('');
  const [term, setTerm] = useState('');
  const [destination, setDestination] = useState(DEFAULT_DESTINATION);
  const [androidPackage, setAndroidPackage] = useState<string>(ANDROID_PACKAGES[0].id);
  const [knownCampaigns, setKnownCampaigns] = useState<string[]>([]);
  const [qrUrl, setQrUrl] = useState<string | null>(null);

  const isFamily = SOURCE_PREFIXES.includes(sourceKey);
  const source = isFamily ? `${sourceKey}${sanitizeTagValue(sourceSuffix)}` : sourceKey;

  const allowedMediums = useMemo(() => mediumsForSource(sourceKey), [sourceKey]);

  // Drop a medium that the newly-picked source does not use, rather than
  // carrying an illegal pair forward invisibly.
  useEffect(() => {
    if (medium && !allowedMediums.includes(medium)) setMedium('');
  }, [allowedMediums, medium]);

  /**
   * Existing campaign slugs, so a second link for the same initiative REUSES
   * the exact string instead of being retyped. This is the single highest-value
   * anti-drift feature on the page — `202608_launch` and `202608_Launch` are
   * two campaigns forever.
   *
   * A failure here is deliberately silent: the datalist is a convenience, and
   * the field stays usable (validated against CAMPAIGN_SLUG_RE) without it.
   */
  const loadCampaigns = useCallback(async () => {
    try {
      const res = await adminFetch(`${apiUrl}/v1/admin/analytics/utm-campaigns`);
      const data: { campaigns?: Array<{ utm_campaign: string }> } = await res.json();
      setKnownCampaigns((data.campaigns ?? []).map((c) => c.utm_campaign).filter(Boolean));
    } catch {
      /* non-blocking — see above */
    }
  }, [apiUrl]);

  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  const verdict = useMemo(
    () => validateUtmTuple({ utm_source: source, utm_medium: medium, utm_campaign: campaign }),
    [source, medium, campaign],
  );

  /** The ordered, sanitized tag set both URLs carry. */
  const params = useMemo(() => {
    const out: Array<[string, string]> = [];
    const push = (k: string, v: string) => {
      const clean = sanitizeTagValue(v);
      if (clean) out.push([k, clean]);
    };
    push('utm_source', source);
    push('utm_medium', medium);
    push('utm_campaign', campaign);
    push('utm_term', term);
    push('utm_content', content);
    // Re-key in REFERRER_UTM_KEYS order so the emitted string is deterministic.
    return REFERRER_UTM_KEYS.map((k) => out.find(([key]) => key === k)).filter(
      (p): p is [string, string] => !!p,
    );
  }, [source, medium, campaign, term, content]);

  /**
   * The destination is free text, so it gets its own verdict. An unparseable
   * URL must surface as a NAMED problem next to the taxonomy issues — rendering
   * an empty card with a working Copy button would hand someone a blank string
   * that looks like the tool simply had nothing to say.
   */
  const destinationUrl = useMemo(() => {
    try {
      const u = new URL(destination.trim());
      return u.protocol === 'http:' || u.protocol === 'https:' ? u : null;
    } catch {
      return null;
    }
  }, [destination]);

  const webUrl = useMemo(() => {
    if (!verdict.ok || !destinationUrl) return '';
    const base = new URL(destinationUrl.toString());
    for (const [k, v] of params) base.searchParams.set(k, v);
    return base.toString();
  }, [destinationUrl, params, verdict.ok]);

  const ready = verdict.ok && !!destinationUrl;

  /**
   * Play Store URL with the whole UTM string URL-encoded ONCE into `referrer`
   * (doc §6b). Play decodes exactly once and hands the result to the app's
   * Install Referrer read verbatim, so a second encoding layer here would
   * arrive as literal `%3D` text.
   */
  const playUrl = useMemo(() => {
    if (!verdict.ok) return '';
    const raw = params.map(([k, v]) => `${k}=${v}`).join('&');
    if (!raw) return '';
    return `${PLAY_DETAILS_PREFIX}?id=${androidPackage}&referrer=${encodeURIComponent(raw)}`;
  }, [params, androidPackage, verdict.ok]);

  const copy = (value: string, what: string) => {
    navigator.clipboard?.writeText(value);
    toast({ title: `${what} copied`, description: value });
  };

  const selectCls = 'w-full px-3 py-2 border rounded-md text-sm bg-background';
  const inputCls = selectCls;

  return (
    <div className="p-6 max-w-[1100px] mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Link2 className="w-6 h-6" />
          Campaign Links
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Build a tagged link. Everything here comes from the canonical taxonomy
          (docs/marketing/utm-taxonomy.md) — a value that is not in it cannot be picked.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* ── Inputs ─────────────────────────────────────────────── */}
        <div className="border rounded-lg p-5 bg-card space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="utm-source">
              Source <span className="text-destructive">*</span>
            </label>
            <select
              id="utm-source"
              className={selectCls}
              value={sourceKey}
              onChange={(e) => setSourceKey(e.target.value)}
            >
              <option value="">Select a source…</option>
              {UTM_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              {SOURCE_PREFIXES.map((p) => (
                <option key={p} value={p}>
                  {p}… (name it below)
                </option>
              ))}
            </select>
            {isFamily && (
              <input
                className={`${inputCls} mt-2`}
                value={sourceSuffix}
                placeholder={sourceKey === 'influencer_' ? 'janedoe' : 'acme_media'}
                onChange={(e) => setSourceSuffix(e.target.value)}
                onBlur={(e) => setSourceSuffix(sanitizeTagValue(e.target.value))}
                aria-label="Source name"
              />
            )}
            {source && (
              <p className="text-xs text-muted-foreground mt-1">
                utm_source=<code>{source}</code>
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="utm-medium">
              Medium <span className="text-destructive">*</span>
            </label>
            <select
              id="utm-medium"
              className={selectCls}
              value={medium}
              disabled={!sourceKey}
              onChange={(e) => setMedium(e.target.value)}
            >
              <option value="">{sourceKey ? 'Select a medium…' : 'Pick a source first'}</option>
              {allowedMediums.map((m) => (
                <option key={m} value={m}>
                  {m} → channel {MEDIUM_TO_CHANNEL[m]}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Only the mediums we use with this source are listed — the channel in every report is
              derived from this one parameter.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="utm-campaign">
              Campaign <span className="text-destructive">*</span>
            </label>
            <input
              id="utm-campaign"
              className={inputCls}
              list="known-campaigns"
              value={campaign}
              placeholder="202608_launch"
              onChange={(e) => setCampaign(e.target.value)}
              onBlur={(e) => setCampaign(sanitizeTagValue(e.target.value))}
            />
            <datalist id="known-campaigns">
              {knownCampaigns.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <p className="text-xs text-muted-foreground mt-1">
              <code>YYYYMM_shortname</code>. Reuse an existing slug for the same initiative — vary
              the source/content, never the campaign.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="utm-content">
                Content (optional)
              </label>
              <input
                id="utm-content"
                className={inputCls}
                value={content}
                placeholder="bio_link"
                onChange={(e) => setContent(e.target.value)}
                onBlur={(e) => setContent(sanitizeTagValue(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="utm-term">
                Term (optional)
              </label>
              <input
                id="utm-term"
                className={inputCls}
                value={term}
                placeholder="paid keyword"
                onChange={(e) => setTerm(e.target.value)}
                onBlur={(e) => setTerm(sanitizeTagValue(e.target.value))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="destination">
              Destination URL
            </label>
            <input
              id="destination"
              className={inputCls}
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            />
          </div>
        </div>

        {/* ── Output ─────────────────────────────────────────────── */}
        <div className="space-y-4">
          {!ready && (
            <div className="border rounded-lg p-4 bg-card text-sm">
              <div className="flex items-center gap-2 font-medium mb-2">
                <AlertTriangle className="w-4 h-4 text-destructive" />
                Not ready to publish
              </div>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                {verdict.issues.map((i) => (
                  <li key={i}>{issueText(i, source)}</li>
                ))}
                {!destinationUrl && <li>Destination must be a full http(s) URL.</li>}
              </ul>
            </div>
          )}

          {ready && (
            <>
              <UrlCard
                title="Website link"
                hint="First-party capture reads these on landing and persists first/last touch."
                url={webUrl}
                onCopy={() => copy(webUrl, 'Website link')}
                onQr={() => setQrUrl(webUrl)}
              />

              <div className="border rounded-lg p-4 bg-card space-y-2">
                <label className="block text-xs text-muted-foreground" htmlFor="android-package">
                  Android package
                </label>
                <select
                  id="android-package"
                  className={selectCls}
                  value={androidPackage}
                  onChange={(e) => setAndroidPackage(e.target.value)}
                >
                  {ANDROID_PACKAGES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <UrlCard
                title="Google Play link (install referrer)"
                hint="The whole UTM string is URL-encoded once into referrer= — Play hands it back to the app verbatim, so the install itself is attributed."
                url={playUrl}
                onCopy={() => copy(playUrl, 'Play link')}
                onQr={() => setQrUrl(playUrl)}
              />

              <div className="border rounded-lg p-4 bg-card text-sm space-y-2">
                <div className="font-medium">Apple App Store</div>
                <p className="text-muted-foreground text-xs">
                  The App Store does <strong>not</strong> pass UTMs into the app, so there is no iOS
                  equivalent of the link above. Send iOS traffic to the <em>website</em> link — the
                  web touch is attributed on landing and stitched to the account at signup. Apple&apos;s
                  own <code>?pt=…&amp;ct=…&amp;mt=8</code> tokens report in App Store Connect only and never
                  reach our database.
                </p>
                <code className="block text-xs break-all text-muted-foreground">{APP_STORE_URL}</code>
              </div>
            </>
          )}

          <div className="border rounded-lg p-4 bg-card text-xs text-muted-foreground space-y-1">
            <div className="font-medium text-foreground text-sm">Not selectable, on purpose</div>
            <p>
              <CodeList values={RESERVED_SOURCES} /> are derived channels, never sources — tagging
              one corrupts the channel report.
            </p>
            <p>
              <CodeList values={PROVIDER_SOURCES} /> are emitted by the stores themselves on organic
              installs. Putting one on a link you build makes a campaign install look organic.
            </p>
          </div>
        </div>
      </div>

      {qrUrl && <QrModal url={qrUrl} filename={campaign || 'campaign'} onClose={() => setQrUrl(null)} />}
    </div>
  );
}

/** `a`, `b`, `c` — each value in its own <code>, comma-separated. */
function CodeList({ values }: { values: readonly string[] }) {
  return (
    <>
      {values.map((v, i) => (
        <span key={v}>
          {i > 0 && ', '}
          <code>{v}</code>
        </span>
      ))}
    </>
  );
}

function UrlCard({
  title,
  hint,
  url,
  onCopy,
  onQr,
}: {
  title: string;
  hint: string;
  url: string;
  onCopy: () => void;
  onQr: () => void;
}) {
  return (
    <div className="border rounded-lg p-4 bg-card">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-medium text-sm">{title}</div>
          <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onCopy} title="Copy" className="text-muted-foreground hover:text-foreground p-1">
            <Copy className="w-4 h-4" />
          </button>
          <button onClick={onQr} title="QR code" className="text-muted-foreground hover:text-foreground p-1">
            <QrCode className="w-4 h-4" />
          </button>
        </div>
      </div>
      <code className="block text-xs break-all bg-muted/40 rounded p-2">{url}</code>
    </div>
  );
}

/**
 * QR export for print/offline placements. Same pattern as the referral-link QR
 * modal (pages/referrals/show.tsx) — qrcode.react is already a dependency, so
 * this adds no npm package.
 *
 * Tag the campaign with `utm_medium=qr` before exporting: a printed code cannot
 * be retagged after it goes out.
 */
function QrModal({ url, filename, onClose }: { url: string; filename: string; onClose: () => void }) {
  const canvasWrapRef = useRef<HTMLDivElement>(null);

  const download = () => {
    const canvas = canvasWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `abby-qr-${filename}.png`;
    a.click();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card border rounded-lg p-6 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">QR — {filename}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div ref={canvasWrapRef} className="inline-block bg-white p-4 rounded-lg">
          <QRCodeCanvas value={url} size={512} style={{ width: 224, height: 224 }} level="M" includeMargin />
        </div>
        <p className="text-xs text-muted-foreground mt-3 break-all">{url}</p>
        <button
          onClick={download}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
        >
          Download PNG (print-ready)
        </button>
      </div>
    </div>
  );
}
